'use client';

import {
  createContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { heartBeat } from '@/lib/health';

const HEART_BEAT_INTERVAL = 1000 * 60;

interface HeartBeatContextData {
  lastHeartBeat?: number;
  heartBeat: boolean;
}

const HeartBeatContext = createContext<HeartBeatContextData>({
  heartBeat: false,
});

interface HeartBeatProps {
  children: ReactNode;
}

function HeartBeat({ children }: HeartBeatProps) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [heartBeatContextData, setHeartBeatContextData] =
    useState<HeartBeatContextData>({ heartBeat: false });

  const callHeartBeat = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    heartBeat()
      .then(() => {
        setHeartBeatContextData({ heartBeat: true, lastHeartBeat: Date.now() });
      })
      .catch(() => {
        setHeartBeatContextData({
          heartBeat: false,
          lastHeartBeat: Date.now(),
        });
      });

    intervalRef.current = setInterval(callHeartBeat, HEART_BEAT_INTERVAL);
  };

  useEffect(() => {
    // Defer the first heartbeat to browser idle so the /api/health-status
    // request doesn't compete with critical resources during initial paint.
    // The 60s polling interval is set up after that first call resolves.
    const win = window as Window & {
      requestIdleCallback?: typeof window.requestIdleCallback;
      cancelIdleCallback?: typeof window.cancelIdleCallback;
    };
    let idleHandle: number | undefined;
    let timeoutHandle: number | undefined;
    if (typeof win.requestIdleCallback === 'function') {
      idleHandle = win.requestIdleCallback(() => callHeartBeat(), {
        timeout: 3000,
      });
    } else {
      timeoutHandle = win.setTimeout(() => callHeartBeat(), 1500);
    }
    return () => {
      if (idleHandle !== undefined) {
        win.cancelIdleCallback?.(idleHandle);
      }
      if (timeoutHandle !== undefined) {
        win.clearTimeout(timeoutHandle);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // `callHeartBeat` is intentionally omitted — adding it would reschedule the
    // interval on every render, doubling the polling rate. The effect must only
    // register the idle/timeout once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only registration (see comment above)
  }, []);

  return (
    <HeartBeatContext.Provider value={heartBeatContextData}>
      {children}
    </HeartBeatContext.Provider>
  );
}

export default HeartBeat;
